import "./style.scss";

import { FC } from "react";
import { Pricing } from "shared/config/pricings";
import Button from "shared/ui/Button";
import { ReactComponent as IconCheck } from "assets/img/icons/icon_status-Checked.svg";

interface DetailOffreProps {
    plan: Pricing;
    /** Action principale. Absente, la fiche reste purement informative. */
    onChoisir?: () => void;
    /** L'offre affichée est celle que le client détient déjà : on le signale et
     *  on neutralise l'action, plutôt que de le laisser souscrire deux fois. */
    actuelle?: boolean;
}

/**
 * Fiche détaillée d'une offre : ce qu'elle est, ce qu'elle coûte, et ce qu'elle
 * contient module par module.
 *
 * Chaque module porte son explication : « ID Locker » seul ne dit rien à qui ne
 * connaît pas le produit. C'est la différence avec la liste courte des écrans de
 * choix, qui sert à comparer, pas à comprendre.
 */
const DetailOffre: FC<DetailOffreProps> = ({ plan, onChoisir, actuelle = false }) => {
    // Les offres qui n'ont pas encore de modules détaillés retombent sur leur
    // liste courte : un intitulé sans explication vaut mieux que rien.
    const modules = plan.modules ?? (plan.avantages ?? []).map((titre) => ({ titre, texte: undefined, marqueur: undefined }));

    return (
        <div className={`DetailOffre ${actuelle ? "DetailOffre--actuelle" : ""}`}>
            <div className="DetailOffre__pastilles">
                {plan.badge && <span className="DetailOffre__badge">{plan.badge}</span>}
                {actuelle && (
                    <span className="DetailOffre__actuelle">
                        <IconCheck className="DetailOffre__check" />
                        Votre abonnement actuel
                    </span>
                )}
            </div>

            <h3 className="DetailOffre__titre">{plan.title}</h3>
            {plan.accroche && <p className="DetailOffre__accroche">{plan.accroche}</p>}

            <div className="DetailOffre__tarif">
                <div className="DetailOffre__tarifCorps">
                    <div className="DetailOffre__prixLigne">
                        <span className="DetailOffre__prix">{plan.price} €</span>
                        {plan.uniteTarif && <span className="DetailOffre__unite">/ {plan.uniteTarif}</span>}
                    </div>
                    {plan.sousPrix && <p className="DetailOffre__sousPrix">{plan.sousPrix}</p>}
                    {plan.mentionRecurrence && <p className="DetailOffre__sousPrix">{plan.mentionRecurrence}</p>}
                </div>

                {onChoisir && (
                    <Button
                        label={actuelle ? "Protection déjà active" : (plan.cta ?? "Choisir cette offre")}
                        onClick={onChoisir}
                        disabled={actuelle}
                        data-testid="detail-offre-choisir"
                    />
                )}
            </div>

            {modules.length > 0 && (
                <div className="DetailOffre__modules">
                    {modules.map((module, i) => (
                        <div className="DetailOffre__module" key={i}>
                            <div className="DetailOffre__moduleEntete">
                                <IconCheck className="DetailOffre__check" />
                                <span className="DetailOffre__moduleTitre">{module.titre}</span>
                                {module.marqueur && <span className="DetailOffre__marqueur">{module.marqueur}</span>}
                            </div>
                            {module.texte && <p className="DetailOffre__moduleTexte">{module.texte}</p>}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DetailOffre;
